import { useEffect, useRef, useState } from 'react';

import { Key } from 'antd/lib/table/interface';
import { FollowUpsListController, FollowUpsSelectionAction } from 'fragments/follow-ups-list/interfaces';
import { FollowUpCardFragmentProps } from 'fragments/follow-up-card/interfaces';
import { FollowUpService, useApiFollowUpService } from 'services/followUps/followUp.service';
import { CompleteFollowUpDto, FollowUpDto, FollowUpPaymentStatusEnum } from 'services/followUps/followUp.dto';
import { RentalPaymentService, useApiRentalPaymentService } from 'services/rentalPayments/rentalPayment.service';
import { DownloadableItem } from 'services/rentalPayments/receipt.dto';
import { useMessenger } from 'tools/view-hooks/messenger-hook';
import { useTranslator } from 'tools/view-hooks/translator-hook';
import { useAPIMembershipService } from 'services/memberships/memberships.service';
import { ContractsService, useAPIContractService } from 'services/contracts/contracts.service';
import {
    runFollowUpPaymentMutation,
    subscribeFollowUpPaymentFailure,
} from 'services/followUps/follow-up-payment-coordinator';
import { calculateFollowUpRentUpdateNotice } from 'tools/rent-updates/rent-update-notice';
import { addMonetary, isMonetaryAmount } from 'tools/currency/ars-amount';

const MONTH_NAMES = [
    'Enero',
    'Febrero',
    'Marzo',
    'Abril',
    'Mayo',
    'Junio',
    'Julio',
    'Agosto',
    'Septiembre',
    'Octubre',
    'Noviembre',
    'Diciembre',
];

/* A paid follow up stays on the list — marking a payment is not the same as this card being done with
   — but it is no longer what the manager needs to act on, so the next time the list is built it sinks
   under the still-pending ones of the same period instead of competing with them for the top. Marking
   the payment itself never moves the card: see onFollowUpUpdate for why that has to wait. */
const isPending = (dto: CompleteFollowUpDto): boolean => dto.paymentStatus !== FollowUpPaymentStatusEnum.COMPLETE;

/* Single number per period so periods can be both grouped and ordered by it. Year comes first,
   which is what makes Diciembre 2025 sort before Enero 2026 instead of after it. */
const periodKeyOf = (dto: CompleteFollowUpDto): number => (dto.year ?? 0) * 12 + ((dto.month ?? 1) - 1);

const monthTitleOf = (dto: CompleteFollowUpDto): string => (MONTH_NAMES[(dto.month ?? 1) - 1] ?? '').toUpperCase();

export const useFollowUpsListController = (
    followUpService: FollowUpService = useApiFollowUpService(),
    rentalPaymentService: RentalPaymentService = useApiRentalPaymentService(),
    membershipsService = useAPIMembershipService(),
    messenger = useMessenger(),
    contractsService: ContractsService = useAPIContractService(),
): FollowUpsListController => {
    const { translate } = useTranslator();
    /* State */
    const [isLoading, setIsLoading] = useState(false);
    const [viewModels, setViewModels] = useState<FollowUpCardFragmentProps[]>([]);
    // Keep only stable identities. Card callbacks replace DTOs in viewModels, so retaining the row objects
    // here would leave bulk validation reading the pre-update rent/interest/charge decision.
    const [selectedIds, setSelectedIds] = useState<number[]>([]);
    const [receiptsModalViewModel, setReceiptsModalViewModel] = useState<DownloadableItem[]>([]);
    const [isReceiptsModalVisible, setIsReceiptsModalVisible] = useState(false);
    const [isSettlementsModalVisible, setIsSettlementsModalVisible] = useState(false);
    const [settlementsModalViewModel, setSettlementsModalViewModel] = useState<{
        selectedFollowUpsByOwner: DownloadableItem[];
        currentMonthFollowUpsByOwner: DownloadableItem[];
    }>({ selectedFollowUpsByOwner: [], currentMonthFollowUpsByOwner: [] });
    const [isGenerateFreeReceiptVisible, setIsGenerateFreeReceiptVisible] = useState(false);
    const [total, setTotal] = useState(0);
    const [offset, setOffset] = useState(0);
    const [filters, setFilters] = useState<any | undefined>(undefined);
    // Undefined until the fetch resolves, so the header shows nothing rather than a misleading "0".
    const [contractsCount, setContractsCount] = useState<number | undefined>(undefined);
    const listRequestRef = useRef(0);
    const mountedRef = useRef(true);
    const latestReadRef = useRef<Promise<void>>(Promise.resolve());
    const cancelReadWaitRef = useRef<() => void>(() => undefined);
    const readPendingRef = useRef(false);
    // Only fields published AFTER the winning GET starts may supersede it, never an entire old row.
    const readUpdatesRef = useRef(new Map<number, Partial<CompleteFollowUpDto>>());
    const ownedBatchIdsRef = useRef(new Set<number>());
    const currentQueryRef = useRef({ filters, offset });
    currentQueryRef.current = { filters, offset };
    const currentSelectionRef = useRef({ viewModels, selectedIds });
    currentSelectionRef.current = { viewModels, selectedIds };

    const limitOfPagination = 30;

    /* Listeners */
    useEffect(() => {
        mountedRef.current = true;
        const unsubscribe = subscribeFollowUpPaymentFailure(async (ids) => {
            // Our batch already reconciles inside its lease; do not start a second recovery cycle.
            if (!mountedRef.current || ids.every((id) => ownedBatchIdsRef.current.has(id))) return;
            await showFollowUps();
        });
        return () => {
            mountedRef.current = false;
            ++listRequestRef.current;
            readUpdatesRef.current.clear();
            cancelReadWaitRef.current();
            unsubscribe();
        };
    }, []);

    useEffect(() => {
        showFollowUps().finally();
    }, [filters, offset]);

    useEffect(() => {
        contractsService
            .getAllContracts()
            // Active contracts only — matches how the rest of the app already counts "contracts the
            // user has" (the plan-limit banner, the upgrade screen), not the historical total that
            // /contratos lists (which includes rescinded/finalized ones).
            .then((contracts) => {
                if (mountedRef.current) setContractsCount(contracts.filter((c) => c.contractIsCurrent).length);
            })
            .catch(() => undefined);
    }, []);

    // Keeps every header's contractsCount in sync with the latest fetched value, no matter which of
    // the two resolves first: addHeaders alone only bakes in whatever was already known at fetch time,
    // and the count fetch is a separate, independently-timed request. Re-running on every viewModels
    // change (not just on contractsCount) is what catches a fetch that lands after the follow ups
    // already rendered; returning the same array reference when nothing actually changed keeps this
    // from looping on its own update.
    useEffect(() => {
        if (contractsCount === undefined) return;
        setViewModels((prevState) => {
            if (prevState.every((item) => item.header.contractsCount === contractsCount)) return prevState;
            return prevState.map((item) => ({ ...item, header: { ...item.header, contractsCount } }));
        });
    }, [contractsCount, viewModels]);

    const fetchCanPay = async (): Promise<boolean> => {
        const dto = await membershipsService.getCurrentMembershipDetail();
        return dto.canPay ?? false;
    };

    const showFollowUps = async (): Promise<void> => {
        if (!mountedRef.current) return;
        const cancelPreviousWait = cancelReadWaitRef.current;
        const cancelled = new Promise<void>((resolve) => {
            cancelReadWaitRef.current = resolve;
        });
        const read = Promise.race([loadFollowUps(), cancelled]);
        latestReadRef.current = read;
        cancelPreviousWait();
        await read;
        // Keep reconciliation leases through any superseding page/filter read as well.
        let latest = read;
        while (mountedRef.current && latest !== latestReadRef.current) {
            latest = latestReadRef.current;
            await latest;
        }
    };

    const loadFollowUps = async (): Promise<void> => {
        const request = ++listRequestRef.current;
        const query = currentQueryRef.current;
        readUpdatesRef.current.clear();
        readPendingRef.current = true;
        setIsLoading(true);
        try {
            const canPay = await fetchCanPay();
            if (!mountedRef.current || request !== listRequestRef.current) return;
            const input = { offset: query.offset, limit: limitOfPagination };
            const resp =
                query.filters != undefined
                    ? await followUpService.applyFilters({ ...query.filters, ...input })
                    : await followUpService.getAllFollowUps(input);
            if (!mountedRef.current || request !== listRequestRef.current) return;
            const newViewModels =
                resp.data?.map((dto) =>
                    mapDtoToCardFollowUp({ ...dto, ...readUpdatesRef.current.get(dto.id as number) }, canPay),
                ) ?? [];
            setTotal(resp.total ?? newViewModels.length);
            replaceRows(addHeaders(newViewModels));
        } catch (_error) {
            if (!mountedRef.current || request !== listRequestRef.current) return;
            // Do not leave actionable stale payment rows if authoritative readback fails.
            replaceRows([]);
            setTotal(0);
            setSelectedIds([]);
            currentSelectionRef.current.selectedIds = [];
            messenger.showErrorMessage({ key: 'followUp.error-on-payment-reload' });
        } finally {
            if (mountedRef.current && request === listRequestRef.current) {
                readPendingRef.current = false;
                readUpdatesRef.current.clear();
                setIsLoading(false);
            }
        }
    };

    const reconcileFailedBatch = async (): Promise<void> => {
        if (!mountedRef.current) return;
        // Each backend row has its own transaction: an error is not a rollback.
        // Read only; never resubmit payments, including when the response was lost.
        setSelectedIds([]);
        currentSelectionRef.current = { ...currentSelectionRef.current, selectedIds: [] };
        await showFollowUps();
        if (mountedRef.current) messenger.showErrorMessage({ key: 'followUp.error-on-multiple-pay' });
    };

    /* View Events */
    const onReceiptsModalCancel = () => {
        setIsReceiptsModalVisible(false);
    };
    const onSettlementsModalCancel = () => {
        setIsSettlementsModalVisible(false);
    };

    const mapDtoToCardFollowUp = (dto: CompleteFollowUpDto, canPay: boolean): FollowUpCardFragmentProps => {
        return { key: `${dto.id}`, dto: dto, header: { title: '', headerVisible: false }, canPay, onFollowUpUpdate };
    };

    const newListFromPaymentUpdate = (prevState: FollowUpCardFragmentProps[], updates: FollowUpDto[]) => {
        return prevState.map((item) => {
            const updatedItem = updates.find((i) => i.id === item.dto.id);
            if (updatedItem) {
                return {
                    ...item,
                    dto: {
                        ...item.dto,
                        id: updatedItem.id,
                        paymentStatus: updatedItem.paymentStatus,
                        amountPaid: updatedItem.amountPaid,
                        ...(updatedItem.followUpStatus !== undefined
                            ? { followUpStatus: updatedItem.followUpStatus }
                            : {}),
                    },
                };
            }
            return item;
        });
    };

    const authoritativeTotalOf = (dto: CompleteFollowUpDto): number | null => {
        if (!isMonetaryAmount(dto.rentalPrice, { min: 0.01 })) return null;
        if (dto.interestPrice !== undefined && !isMonetaryAmount(dto.interestPrice)) return null;
        const interest = dto.interestPrice ?? 0;
        return dto.followUpStatus === 'expired' && dto.dontCharge !== true
            ? addMonetary(dto.rentalPrice, interest)
            : dto.rentalPrice;
    };

    const requiresPriceUpdate = (dto: CompleteFollowUpDto): boolean =>
        calculateFollowUpRentUpdateNotice({
            isPeriodicContract: dto.contract?.isNewLawContract,
            upgradeable: dto.upgradeable,
            followUpMonth: dto.month,
            followUpYear: dto.year,
            initialDate: dto.contract?.initialDate,
            durationMonths: dto.contract?.durationMonths,
            updateFrequency: dto.contract?.updateFrequency,
            rentalUpdates: dto.contract?.rentalUpdates,
            contractCreatedAt: dto.contract?.createdAt,
        }).showUpdateAction;

    const isAuthoritativeBulkResponse = (
        response: unknown,
        selected: FollowUpCardFragmentProps[],
        isPaid: boolean,
    ): response is FollowUpDto[] => {
        if (!Array.isArray(response) || response.length !== selected.length) return false;
        const selectedById = new Map(selected.map((row) => [row.dto.id, row.dto]));
        const seen = new Set<number>();
        return response.every((value: unknown) => {
            if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
            const dto = value as FollowUpDto;
            if (!Number.isSafeInteger(dto.id) || seen.has(dto.id as number)) return false;
            const source = selectedById.get(dto.id);
            if (!source || !isMonetaryAmount(dto.amountPaid)) return false;
            // Preserve the server's semantic badge together with the paid ledger when supplied.
            // Legacy absence remains optional, but a present contradictory/malformed status fails closed.
            if (
                dto.followUpStatus !== undefined &&
                (isPaid
                    ? dto.followUpStatus !== 'isPaid'
                    : dto.followUpStatus !== 'expired' && dto.followUpStatus !== 'pending')
            )
                return false;
            const total = authoritativeTotalOf(source);
            if (total === null) return false;
            seen.add(dto.id as number);
            return isPaid
                ? dto.paymentStatus === FollowUpPaymentStatusEnum.COMPLETE && dto.amountPaid >= total
                : dto.paymentStatus === FollowUpPaymentStatusEnum.INCOMPLETE && dto.amountPaid === 0;
        });
    };

    const resolveSelectedRows = (ids: number[]): FollowUpCardFragmentProps[] => {
        const currentById = new Map(currentSelectionRef.current.viewModels.map((row) => [row.dto.id, row]));
        return ids
            .map((id) => currentById.get(id))
            .filter((row): row is FollowUpCardFragmentProps => row !== undefined);
    };

    const mutateSelectedPayments = async (isPaid: boolean): Promise<void> => {
        const ids = [...currentSelectionRef.current.selectedIds];
        const selected = resolveSelectedRows(ids);
        if (
            !selected.length ||
            selected.length !== ids.length ||
            ids.some((id) => !Number.isSafeInteger(id) || (id as number) <= 0) ||
            selected.some((row) => authoritativeTotalOf(row.dto) === null)
        ) {
            messenger.showErrorMessage({ key: 'followUp.error-on-multiple-pay' });
            return;
        }
        if (isPaid && selected.some((row) => requiresPriceUpdate(row.dto))) {
            messenger.showErrorMessage({ key: 'follow-ups.must-update-price-first' });
            return;
        }

        let ownsLease = false;
        try {
            await runFollowUpPaymentMutation(ids as number[], async () => {
                ownsLease = true;
                ids.forEach((id) => ownedBatchIdsRef.current.add(id));
                try {
                    const response = await rentalPaymentService.addMultiplePayments(
                        (ids as number[]).map((idFollowUp) => ({ idFollowUp, isPaid })),
                    );
                    const latestSelectedIds = currentSelectionRef.current.selectedIds;
                    const selectionIsCurrent =
                        latestSelectedIds.length === ids.length && ids.every((id) => latestSelectedIds.includes(id));
                    const currentSelected = resolveSelectedRows(ids);
                    if (
                        !selectionIsCurrent ||
                        currentSelected.length !== ids.length ||
                        !isAuthoritativeBulkResponse(response, currentSelected, isPaid)
                    ) {
                        await reconcileFailedBatch();
                        return false;
                    }
                    if (!mountedRef.current) return false;
                    const updatedRows = newListFromPaymentUpdate(currentSelectionRef.current.viewModels, response);
                    updatedRows.forEach((row) => onFollowUpUpdate(row.dto));
                    setSelectedIds([]);
                    currentSelectionRef.current.selectedIds = [];
                    return true;
                } catch (_error) {
                    await reconcileFailedBatch();
                    return false;
                }
            });
        } finally {
            if (ownsLease) ids.forEach((id) => ownedBatchIdsRef.current.delete(id));
        }
    };

    const payRents = () => void mutateSelectedPayments(true);

    const dontPayRents = () => void mutateSelectedPayments(false);
    const issueRentalReceipt = () => {
        const followUpIdsArray = [...selectedIds];
        rentalPaymentService
            .onIssueReceipt({ followUpIds: followUpIdsArray })
            .then((dto) => {
                setIsReceiptsModalVisible(true);
                setReceiptsModalViewModel(dto);
            })
            .catch(() => {
                messenger.showErrorMessage({ key: 'followUp.error-on-issue-receipts' });
            });
    };
    const issueSettlements = () => {
        const followUpIdsArray = [...selectedIds];
        rentalPaymentService
            .onIssueSettlements({
                followUpIds: followUpIdsArray,
                allSelected: selectedIds.length === viewModels.length,
            })
            .then((dto) => {
                setIsSettlementsModalVisible(true);
                setSettlementsModalViewModel(dto);
            })
            .catch(() => {
                messenger.showErrorMessage({ key: 'followUp.error-on-issue-receipts' });
            });
    };
    const rowSelection = {
        hideSelectAll: false,
        selectedRowKeys: selectedIds.map(String),
        onChange: (_selectedKeys: Key[], selectedRows: FollowUpCardFragmentProps[]) => {
            setSelectedIds(selectedRows.map((row) => row.dto.id as number));
        },
    };
    const onGenerateFreeReceiptAdded = () => {
        setIsGenerateFreeReceiptVisible(false);
    };
    const onGenerateFreeReceiptPressed = () => {
        setIsGenerateFreeReceiptVisible(true);
    };

    const onGenerateFreeReceiptCanceled = () => {
        setIsGenerateFreeReceiptVisible(false);
    };

    const selectionActions: FollowUpsSelectionAction[] = [
        {
            key: 'pay-multiple',
            text: translate({ key: 'follow-ups.mark-as-paid' }),
            group: 'payment',
            onSelect: payRents,
        },
        {
            key: 'dont-pay-multiple',
            text: translate({ key: 'follow-ups.mark-as-dont-paid' }),
            group: 'payment',
            onSelect: dontPayRents,
        },
        {
            key: 'issue-receipt',
            text: translate({ key: 'follow-ups.generate-rent-receipt' }),
            group: 'receipt',
            onSelect: issueRentalReceipt,
        },
        {
            key: 'issue-settlement',
            text: translate({ key: 'follow-ups.generate-settlements' }),
            group: 'receipt',
            onSelect: issueSettlements,
        },
        {
            key: 'issue-free-receipt',
            text: translate({ key: 'follow-ups.generate-free-receipt' }),
            group: 'receipt',
            onSelect: onGenerateFreeReceiptPressed,
        },
    ];

    /* Groups every follow up under one header per period, oldest period first: the months still owed
       pile up at the top and the current month closes the list, which is the last thing the manager
       reads. Within a period, pending cards come first and paid ones sink to the bottom of that same
       group — Array#sort is stable, so paid-among-paid and pending-among-pending keep whatever order
       the backend returned. Only ever run on a fresh fetch: see onFollowUpUpdate below for why a live
       status change must not run this again. */
    const addHeaders = (vm: FollowUpCardFragmentProps[]): FollowUpCardFragmentProps[] => {
        const periodKeys: number[] = [];
        const groups: { [periodKey: number]: FollowUpCardFragmentProps[] } = {};

        vm.forEach((item) => {
            const periodKey = periodKeyOf(item.dto);
            if (!groups[periodKey]) {
                groups[periodKey] = [];
                periodKeys.push(periodKey);
            }
            groups[periodKey].push(item);
        });

        periodKeys.sort((a, b) => a - b);

        return periodKeys.reduce<FollowUpCardFragmentProps[]>((withHeaders, periodKey) => {
            const sortedGroup = [...groups[periodKey]].sort(
                (a, b) => Number(isPending(b.dto)) - Number(isPending(a.dto)),
            );
            const group = sortedGroup.map((item, indexInPeriod) => ({
                ...item,
                header: {
                    title: monthTitleOf(item.dto),
                    /* Only the first card of the period carries the header row. */
                    headerVisible: indexInPeriod === 0,
                    contractsCount,
                },
            }));
            return withHeaders.concat(group);
        }, []);
    };

    /* Bubbled up from inside a single card (red/green icon, amount field): merges the fresh dto into
       the row it belongs to, in place — the card must stay exactly where it is for the rest of this
       session. Re-sorting into the pending/paid buckets only happens the next time the list is fetched
       (a reload, a filter change, a page change), never right after a status change. The card is never
       removed here either way: it is still on the list, just not yet moved. */
    const onFollowUpUpdate = (dto: CompleteFollowUpDto) => {
        if (!mountedRef.current) return;
        const previous = currentSelectionRef.current.viewModels.find((item) => item.dto.id === dto.id)?.dto;
        if (!previous) return; // An old card cannot resurrect an off-page/failed-read row.
        const changes = Object.fromEntries(
            Object.entries(dto).filter(
                ([key, value]) => key !== 'id' && value !== previous[key as keyof CompleteFollowUpDto],
            ),
        );
        if (readPendingRef.current) {
            readUpdatesRef.current.set(dto.id as number, {
                ...readUpdatesRef.current.get(dto.id as number),
                ...changes,
            });
        }
        replaceRows(
            currentSelectionRef.current.viewModels.map((item) =>
                item.dto.id === dto.id ? { ...item, dto: { ...item.dto, ...changes } } : item,
            ),
        );
    };

    const replaceRows = (rows: FollowUpCardFragmentProps[]) => {
        currentSelectionRef.current = { ...currentSelectionRef.current, viewModels: rows };
        setViewModels(rows);
    };

    const onChangePage = (page: number) => {
        const currentPage = page - 1;
        const newOffset = currentPage * limitOfPagination;
        setOffset(newOffset);
    };

    const onFilterChange = (newFilters: any) => {
        setFilters(newFilters);
    };

    const onCleanFilter = () => {
        setFilters(undefined);
    };

    // Return state and events
    return {
        isGenerateFreeReceiptVisible,
        isReceiptsModalVisible,
        isSettlementsModalVisible,
        receiptsModalViewModel,
        settlementsModalViewModel,
        rowSelection,
        selectionActions,
        viewModels,
        isLoading,
        currentPage: offset / limitOfPagination + 1,
        limitOfPagination,
        total,
        isFiltering: filters != undefined,
        onGenerateFreeReceiptCanceled,
        onGenerateFreeReceiptAdded,
        onGenerateFreeReceiptPressed,
        onReceiptsModalCancel,
        onSettlementsModalCancel,
        onChangePage,
        onFilterChange,
        onCleanFilter,
    };
};
